import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import Select from "react-select";
import useDarkMode from "../../hooks/useDarkMode";
import DropDownStyles, {
  DropDownStylesDark,
} from "../staff/dropdownStyles";
import useThemeColor from "../../hooks/useThemeColor";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import moment from "moment";
import "./TableSpentReport.css";

const toValidDate = (value) => {
  if (value == null) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return date instanceof Date && !isNaN(date.getTime()) ? date : null;
};

const normalizeCurrencyCode = (currencyCode) => {
  const map = {
    "£": "GBP",
    "€": "EUR",
    "€ ": "EUR",
    $: "USD",
    "₹": "INR",
    "₨": "PKR",
    Rs: "PKR",
  };

  if (!currencyCode) return "GBP";
  const trimmed = currencyCode.trim();
  if (map[trimmed]) return map[trimmed];
  if (/^[A-Za-z]{3}$/.test(trimmed)) return trimmed.toUpperCase();
  return "GBP";
};

const currencyFormatter = (currencyCode) => {
  const safeCode = normalizeCurrencyCode(currencyCode);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: safeCode,
    minimumFractionDigits: 2,
  });
};

const formatNumber = (value) =>
  new Intl.NumberFormat().format(value ?? 0);

const summarizeOrderCovers = (orders = []) =>
  orders.reduce((acc, order) => acc + (order.covers ?? 0), 0);

const TableSpentReport = () => {
  const CompID = useSelector(
    (state) => state.auth.user?.userData?.compID
  );
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = useMemo(
    () => (isDarkMode ? DropDownStylesDark : DropDownStyles),
    [isDarkMode]
  );
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = normalizeCurrencyCode(getCompCurrency());
  const currency = useMemo(
    () => currencyFormatter(compCurrency),
    [compCurrency]
  );

  const [serviceAreas, setServiceAreas] = useState([
    { value: "all", label: "All Service Areas" },
  ]);
  const [selectedArea, setSelectedArea] = useState(serviceAreas[0]);
  const [interval, setInterval] = useState({
    value: 60,
    label: "Every 60 min",
  });
  const [rangeDates, setRangeDates] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [summary, setSummary] = useState(null);
  const [timeBuckets, setTimeBuckets] = useState([]);
  const [tableDetails, setTableDetails] = useState([]);
  const [viewMode, setViewMode] = useState("table"); // table | time
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const intervalOptions = [
    { value: 15, label: "Every 15 min" },
    { value: 30, label: "Every 30 min" },
    { value: 60, label: "Every 60 min" },
    { value: 90, label: "Every 90 min" },
  ];

  const fetchServiceAreas = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_SERVICE_AREAS, {
        params: { CompID, Search: "" },
      });
      const raw = response.data?.data || response.data || [];
      const mapped = Array.isArray(raw)
        ? raw.map((area) => ({
            value:
              area.id ??
              area.ID ??
              area.sA_ID ??
              area.sa_ID ??
              area.SA_ID,
            label: area.sA_Name ?? area.SA_Name ?? area.name ?? "Service Area",
          }))
        : [];
      const unique = mapped.filter((option) => option.value);
      const options = [
        { value: "all", label: "All Service Areas" },
        ...unique,
      ];
      setServiceAreas(options);
      setSelectedArea((prev) =>
        options.find((opt) => opt.value === prev?.value) || options[0]
      );
    } catch (err) {
      console.error("Failed to load service areas", err);
    }
  }, [CompID]);

  const fetchReport = useCallback(async () => {
    if (!CompID) return;
    setLoading(true);
    setError("");
    try {
      const startDate = rangeDates[0];
      const endDate = rangeDates[1] || rangeDates[0];

      const params = {
        CompID,
        IntervalMinutes: interval.value,
      };

      if (startDate) {
        params.StartDate = moment(startDate).format("YYYY-MM-DD HH:mm:ss");
      }
      if (endDate) {
        params.EndDate = moment(endDate).format("YYYY-MM-DD HH:mm:ss");
      }
      if (selectedArea?.value && selectedArea.value !== "all") {
        params.ServiceAreaID = selectedArea.value;
      }

      const response = await axiosInstance.get(
        APIs.GET_TABLE_SPENT_REPORT,
        { params }
      );

      if (response.data?.status) {
        const payload = response.data.data || response.data || {};
        setSummary(payload.summary || payload.Summary || null);
        setTimeBuckets(payload.timeBuckets || payload.TimeBuckets || []);
        const tables =
          payload.tableDetails || payload.TableDetails || [];
        const normalized = Array.isArray(tables)
          ? tables.map((table) => ({
              ...table,
              Orders: table.orders || table.Orders || [],
            }))
          : [];
        setTableDetails(normalized);
        setLastUpdated(new Date());
      } else {
        setSummary(null);
        setTimeBuckets([]);
        setTableDetails([]);
        setError(
          response.data?.message ||
            "Unable to fetch table spent report."
        );
      }
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Unable to fetch table spent report."
      );
      setSummary(null);
      setTimeBuckets([]);
      setTableDetails([]);
    } finally {
      setLoading(false);
    }
  }, [CompID, interval.value, rangeDates, selectedArea]);

  useEffect(() => {
    fetchServiceAreas();
  }, [fetchServiceAreas]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleRangeChange = (dates) => {
    const [newStart, newEnd] = dates || [null, null];
    if (!newStart && !newEnd) return;

    let effectiveStart =
      newStart && moment(newStart).isValid() ? newStart : null;
    let effectiveEnd = newEnd && moment(newEnd).isValid() ? newEnd : null;

    if (effectiveStart && effectiveEnd && effectiveStart > effectiveEnd) {
      [effectiveStart, effectiveEnd] = [effectiveEnd, effectiveStart];
    } else if (effectiveStart && !effectiveEnd) {
      effectiveEnd = new Date(effectiveStart.getTime());
      effectiveEnd.setHours(23, 59, 59, 999);
    }

    setRangeDates([effectiveStart, effectiveEnd]);
  };

  const renderSummary = () => {
    if (!summary) {
      return (
        <div className="empty-state">
          No data found for the selected filters.
        </div>
      );
    }

    const cards = [
      {
        icon: "fa-table",
        label: "Total Tables",
        value: formatNumber(summary.totalTables),
        sub: `Active: ${formatNumber(summary.activeTables)}`,
      },
      {
        icon: "fa-chair",
        label: "Total Covers Capacity",
        value: formatNumber(summary.totalCoversCapacity),
      },
      {
        icon: "fa-users",
        label: "Total Covers Spent",
        value: formatNumber(summary.totalCoversSpent),
      },
      {
        icon: "fa-coins",
        label: "Total Amount Spent",
        value: currency.format(summary.totalAmountSpent || 0),
      },
      {
        icon: "fa-divide",
        label: "Avg Spend / Cover",
        value: currency.format(summary.averageSpendPerCover || 0),
      },
    ];

    return (
      <div
        className="mb-4"
        style={{ position: "relative", zIndex: 0, isolation: "isolate" }}
      >
        <div className="d-flex gap-3 flex-wrap">
          {cards.map((card) => (
            <div
              key={card.label}
              className="flex-grow-1"
              style={{
                minWidth: "170px",
                backgroundColor: themeColor,
                borderRadius: "8px",
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                color: "white",
              }}
            >
              <i
                className={`fa ${card.icon}`}
                style={{ fontSize: "22px", opacity: 0.9 }}
              ></i>
              <div>
                <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                  {card.value}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.85 }}>
                  {card.label}
                </div>
                {card.sub && (
                  <div style={{ fontSize: "11px", opacity: 0.75 }}>
                    {card.sub}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTimeBuckets = () => {
    if (!timeBuckets.length) {
      return (
        <div className="empty-state">
          No time buckets available for this selection.
        </div>
      );
    }

    return (
      <div className="time-grid">
        {timeBuckets.map((bucket, index) => (
          <div className="time-card" key={`${bucket.intervalStart}-${index}`}>
            <div className="time-card-header">
              <h5>
                {moment(bucket.intervalStart).format("MMM DD, YYYY")} ·{" "}
                {moment(bucket.intervalStart).format("hh:mm A")}
              </h5>
              <strong>{currency.format(bucket.totalAmount || 0)}</strong>
            </div>
            <div className="time-card-meta">
              <span>
                <i className="fa fa-chair me-1"></i>
                Covers: {formatNumber(bucket.totalCovers)}
              </span>
              <span>
                <i className="fa fa-table me-1"></i>
                Tables: {formatNumber(bucket.tableCount)}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTables = () => {
    if (!tableDetails.length) {
      return (
        <div className="empty-state">
          No table information available for this selection.
        </div>
      );
    }

    return (
      <div className="table-grid">
        {tableDetails.map((table) => (
          <div className="table-card" key={table.tableId || table.TableId || table.t_ID}>
            <div className="table-card-header">
              <div>
                <h4>{table.tableName || table.TableName}</h4>
                <small className="text-muted">
                  {table.serviceAreaName || "Unknown Area"}
                </small>
              </div>
              <strong>{currency.format(table.totalAmount || 0)}</strong>
            </div>
            <div className="table-card-stats">
              <span>
                <i className="fa fa-chair me-2"></i>
                Seats: {formatNumber(table.seats)}
              </span>
              <span>
                <i className="fa fa-users me-2"></i>
                Covers: {formatNumber(table.totalCovers)}
              </span>
              <span>
                <i className="fa fa-receipt me-2"></i>
                Orders: {formatNumber(table.orderCount)}
              </span>
            </div>
            {table.Orders && table.Orders.length > 0 && (
              <div className="orders-list">
                {table.Orders.map((order, idx) => (
                  <div className="order-item" key={`${order.orderNumber}-${idx}`}>
                    <div>
                      <strong>{order.orderNumber}</strong>
                      <div className="text-muted">
                        {moment(order.startTime).format("MMM DD, hh:mm A")}
                      </div>
                    </div>
                    <div>
                      <strong>{currency.format(order.amount || 0)}</strong>
                      <div className="text-muted">
                        Covers: {formatNumber(order.covers)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="table-spent-page">
      {loading && <Preloader text="Fetching table spent report..." />}

      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <a href="#/">Home</a>
        </li>
        <li className="breadcrumb-item">
          <a href="#/report">Reports</a>
        </li>
        <li className="breadcrumb-item active">Table Spent Report</li>
      </ol>
      <h1 className="page-header">Table Spent Report</h1>

      {/* Filters */}
      <div
        className="mb-4"
        style={{ position: "relative", zIndex: 100, overflow: "visible" }}
      >
        <div
          className="card filter-card"
          style={{ position: "relative", zIndex: 100, overflow: "visible" }}
        >
          <div className="card-body p-4">
            <div
              className="filter-header"
              style={{
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className="fa fa-filter me-2"></i>
                  <h6 className="mb-0 fw-semibold">Filter Table Spent Report</h6>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="clear-btn"
                    onClick={() => {
                      setRangeDates([
                        moment().startOf("day").toDate(),
                        moment().endOf("day").toDate(),
                      ]);
                      setSelectedArea(serviceAreas[0]);
                      setInterval({ value: 60, label: "Every 60 min" });
                    }}
                    title="Clear all filters"
                    style={{ height: "32px" }}
                  >
                    <i className="fa fa-times me-1"></i>
                    Clear All
                  </button>
                </div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-xl-4 col-lg-6 col-md-6">
                <label className="filter-label">
                  <i className="fa fa-table"></i>
                  Service Area
                </label>
                <div className="filter-control">
                  <Select
                    value={selectedArea}
                    onChange={(option) => setSelectedArea(option)}
                    options={serviceAreas}
                    styles={dropdownStyles}
                    placeholder="All Service Areas"
                    className="filter-dropdown"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              </div>

              <div className="col-xl-4 col-lg-6 col-md-6">
                <label className="filter-label">
                  <i className="fa fa-clock"></i>
                  Interval
                </label>
                <div className="filter-control">
                  <Select
                    value={interval}
                    onChange={(option) => setInterval(option)}
                    options={intervalOptions}
                    styles={dropdownStyles}
                    className="filter-dropdown"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              </div>

              <div className="col-xl-4 col-lg-6 col-md-6">
                <label className="filter-label">
                  <i className="fa fa-calendar"></i>
                  Select Date &amp; Time Range
                </label>
                <div
                  className="filter-control"
                  style={{ position: "relative", zIndex: 102 }}
                >
                  <DateRangePicker
                    startDate={toValidDate(rangeDates[0])}
                    endDate={toValidDate(rangeDates[1])}
                    onChange={handleRangeChange}
                    placeholder="Select Date & Time Range"
                    className="filter-dropdown"
                    allowSingleDate
                    allowDateRange
                    showTimeSelection={true}
                  />
                </div>
              </div>
            </div>

            <div className="bottom-controls mt-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="control-label mb-0">
                    <i className="fa fa-info-circle"></i>
                    Showing:
                  </span>
                  <span className="stats-text">
                    {tableDetails.length} tables
                  </span>
                  {lastUpdated && (
                    <small className="text-muted ms-2">
                      Updated {moment(lastUpdated).fromNow()}
                    </small>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="refresh-btn"
                    onClick={fetchReport}
                    disabled={loading}
                  >
                    <i
                      className={`fa ${
                        loading ? "fa-spinner fa-spin" : "fa-sync-alt"
                      } me-1`}
                    ></i>
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {renderSummary()}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">View Mode</h5>
        <div className="view-toggle">
          <button
            type="button"
            className={viewMode === "table" ? "active" : ""}
            onClick={() => setViewMode("table")}
          >
            Table Base
          </button>
          <button
            type="button"
            className={viewMode === "time" ? "active" : ""}
            onClick={() => setViewMode("time")}
          >
            Time Based
          </button>
        </div>
      </div>

      {viewMode === "table" ? renderTables() : renderTimeBuckets()}
    </div>
  );
};

export default TableSpentReport;

